import { useState } from "react";
import type { JsonSchema, FormValue } from "../lib/schema-form/types";
import type { Widget, WidgetContext } from "../lib/schema-form/types";
import {
  fieldsOf,
  isRequired,
  discriminantKey,
  activeBranch,
  branchLiterals,
  isPasswordField,
  isFieldHidden,
} from "../lib/schema-form/walk";
import { FormField, TextInput, TextArea } from "./primitives/FormField";

// Render a string field as a multi-line textarea when the schema asks for it
// (`format: "multiline"`) or the field name is a known multi-line value — most
// importantly a PEM private key, which a single-line input would corrupt by
// dropping its newlines. Multiline wins over password masking so the operator
// can see and verify a pasted key.
function isMultilineField(name: string, schema: JsonSchema): boolean {
  if (schema.format === "multiline") return true;
  return /privatekey|publickey|(^|_)pem$|certificate|(^|_)cert$/i.test(name);
}

interface Props {
  schema: JsonSchema;
  value: FormValue;
  onChange: (v: FormValue) => void;
  errors?: Record<string, string>;
  /**
   * Top-level forms split fields into required (shown) and optional
   * (collapsed under "Additional settings"). Nested object fieldsets opt
   * out — once the operator has opened a nested block, every field within
   * it renders inline so they can see what they're configuring.
   * Discriminator branches stay top-level so e.g. `webhookClientSecret`
   * tucks under Additional settings after picking PRIVATE-APP.
   */
  topLevel?: boolean;
  /**
   * Mask string fields whose name looks secret-y (token/secret/apiKey/…).
   * Only the secret form opts in — config + transform params are never
   * sensitive by design, so a config field like `secretRoleArn` shouldn't be
   * hidden behind a password input. An explicit `format: "password"` on the
   * schema always masks regardless. Default false.
   */
  maskSecrets?: boolean;
  /**
   * True when an ancestor object field is itself optional. A field marked
   * `required` inside an optional block isn't unconditionally required — the
   * whole block can be omitted — so we render its marker as "required if set"
   * rather than a bare "*", which would otherwise read as "you must fill this"
   * (DEV / task #62).
   */
  ancestorOptional?: boolean;
  /** Field-name → bespoke renderer. A field whose key is present here bypasses
   *  the generic renderer entirely. Keyed by field name (e.g. "enrichments"). */
  widgets?: Record<string, Widget>;
  /** Opaque data passed straight to every widget (e.g. { connections, discover }). */
  widgetContext?: WidgetContext;
}

/** The field label + a required marker that distinguishes unconditional
 *  requirements (`*`) from "required only if you fill in this optional block"
 *  (a muted "required if set"). */
function FieldLabel({ name, required, conditional }: { name: string; required: boolean; conditional: boolean }) {
  return (
    <>
      {name}
      {required && !conditional && <span className="field__required" aria-hidden="true"> *</span>}
      {required && conditional && <span className="field__conditional"> · required if set</span>}
    </>
  );
}

/** Append "default: X" to the hint for primitives so operators see what
 *  value they get if they leave the field blank. Object/array defaults are
 *  rendered inside their own nested form so we skip them here. */
function fieldHint(schema: JsonSchema): string | undefined {
  const parts: string[] = [];
  if (schema.description) parts.push(schema.description);
  const d = schema.default;
  if (d !== undefined && (typeof d === "string" || typeof d === "number" || typeof d === "boolean")) {
    parts.push(`default: ${String(d)}`);
  }
  return parts.length > 0 ? parts.join(" • ") : undefined;
}

export function SchemaForm({ schema, value, onChange, errors, topLevel = true, maskSecrets = false, ancestorOptional = false, widgets, widgetContext }: Props) {
  const dKey = discriminantKey(schema);
  if (dKey) {
    const literals = branchLiterals(schema, dKey);
    const current = (value as Record<string, unknown>)[dKey] as string | undefined;
    const active = activeBranch(schema, dKey, current);
    if (!active) return null;
    return (
      <div className="schema-form__union">
        <div role="tablist" className="schema-form__tabs uppercase-label">
          {literals.map((literal) => (
            <button
              key={literal}
              role="tab"
              type="button"
              className={`schema-form__tab${current === literal ? " is-active" : ""}`}
              aria-selected={current === literal}
              onClick={() => onChange({ ...value, [dKey]: literal })}
            >
              {literal}
            </button>
          ))}
        </div>
        <SchemaForm schema={active} value={value} onChange={onChange} errors={errors} topLevel={topLevel} maskSecrets={maskSecrets} ancestorOptional={ancestorOptional} widgets={widgets} widgetContext={widgetContext} />
      </div>
    );
  }

  const allFields = fieldsOf(schema).filter(([, child]) => !isFieldHidden(child));
  const requiredFields = allFields.filter(([key]) => isRequired(schema, key));
  const optionalFields = allFields.filter(([key]) => !isRequired(schema, key));

  const renderField = ([key, child]: [string, JsonSchema]) => {
    const Widget = widgets?.[key];
    if (Widget) {
      return (
        <Widget
          key={key}
          schema={child}
          value={(value as Record<string, unknown>)[key]}
          onChange={(v) => onChange({ ...value, [key]: v })}
          errors={errors}
          context={widgetContext}
        />
      );
    }
    return (
      <FieldRow
        key={key}
        name={key}
        schema={child}
        required={isRequired(schema, key)}
        ancestorOptional={ancestorOptional}
        maskSecrets={maskSecrets}
        value={(value as Record<string, unknown>)[key]}
        error={errors?.[key]}
        errors={errors}
        onChange={(v) => onChange({ ...value, [key]: v })}
        widgets={widgets}
        widgetContext={widgetContext}
      />
    );
  };

  // Nested object: render flat (no split). Once the operator has opened a
  // block, hiding half its fields behind another disclosure adds clicks
  // without making the form clearer.
  if (!topLevel) {
    return <>{allFields.map(renderField)}</>;
  }

  // Top-level: split. Required fields render normally; optional fields are
  // tucked behind a collapsed "Additional settings" disclosure so operators
  // aren't intimidated by a wall of fields they don't need to fill.
  // Edge cases: if there's nothing optional, skip the disclosure entirely;
  // if there's nothing required, render optionals inline (no point hiding
  // every field the operator has).
  if (optionalFields.length === 0) {
    return <>{requiredFields.map(renderField)}</>;
  }
  if (requiredFields.length === 0) {
    return <>{optionalFields.map(renderField)}</>;
  }

  return (
    <>
      {requiredFields.map(renderField)}
      <details className="schema-form__additional">
        <summary className="schema-form__additional-summary uppercase-label">
          Additional settings
        </summary>
        <div className="schema-form__additional-body">
          {optionalFields.map(renderField)}
        </div>
      </details>
    </>
  );
}

function FieldRow({
  name,
  schema,
  required,
  ancestorOptional = false,
  maskSecrets = false,
  value,
  error,
  errors,
  onChange,
  widgets,
  widgetContext,
}: {
  name: string;
  schema: JsonSchema;
  required: boolean;
  ancestorOptional?: boolean;
  maskSecrets?: boolean;
  value: unknown;
  error?: string;
  errors?: Record<string, string>;
  onChange: (v: unknown) => void;
  widgets?: Record<string, Widget>;
  widgetContext?: WidgetContext;
}) {
  const hint = fieldHint(schema);
  const label = <FieldLabel name={name} required={required} conditional={ancestorOptional} />;
  if (schema.type === "boolean") {
    return (
      <FormField label={label} hint={hint} error={error}>
        <input
          type="checkbox"
          aria-label={name}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      </FormField>
    );
  }
  if (schema.enum) {
    // Surface the schema default *in the dropdown itself*. The server applies
    // `default` when the field is left blank, so a bare "—" placeholder hides
    // which value actually gets saved — exactly the trap that silently set a
    // source's `stream` to its default. Showing "default: X" makes the
    // do-nothing choice explicit.
    const placeholder =
      schema.default !== undefined && schema.default !== null
        ? `default: ${String(schema.default)}`
        : "—";
    return (
      <FormField label={label} hint={hint} error={error}>
        <select
          className="field__input"
          aria-label={name}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder}</option>
          {schema.enum.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </FormField>
    );
  }
  if (schema.type === "array") {
    return (
      <ArrayField
        name={name}
        schema={schema}
        required={required}
        ancestorOptional={ancestorOptional}
        value={value as unknown[] | undefined}
        error={error}
        onChange={onChange}
        widgets={widgets}
        widgetContext={widgetContext}
        errors={errors}
      />
    );
  }
  if (schema.type === "object") {
    // Record / map (additionalProperties on object without explicit properties)
    if (
      (!schema.properties || Object.keys(schema.properties).length === 0) &&
      schema.additionalProperties && typeof schema.additionalProperties === "object"
    ) {
      return (
        <RecordField
          name={name}
          schema={schema}
          required={required}
          ancestorOptional={ancestorOptional}
          value={(value as Record<string, unknown>) ?? {}}
          error={error}
          onChange={onChange}
        />
      );
    }
    // A nested object that is itself optional makes everything inside
    // conditional — propagate `ancestorOptional` so inner required markers
    // read "required if set" (task #62). The legend flags the group as optional.
    const childOptional = ancestorOptional || !required;
    return (
      <fieldset className="schema-form__nested">
        <legend className="uppercase-label">
          <FieldLabel name={name} required={required} conditional={ancestorOptional} />
          {!required && !ancestorOptional && <span className="field__conditional"> (optional)</span>}
        </legend>
        <SchemaForm
          schema={schema}
          value={(value as FormValue) ?? {}}
          onChange={(v) => onChange(v)}
          topLevel={false}
          maskSecrets={maskSecrets}
          ancestorOptional={childOptional}
          widgets={widgets}
          widgetContext={widgetContext}
        />
      </fieldset>
    );
  }
  if (schema.type === "number" || schema.type === "integer") {
    return (
      <FormField label={label} hint={hint} error={error}>
        <TextInput
          type="number"
          aria-label={name}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === "" ? undefined : Number(v));
          }}
        />
      </FormField>
    );
  }

  // default — string. An explicit `format: "password"` always masks; the
  // name heuristic (token/secret/apiKey/…) only applies inside the secret form
  // (maskSecrets) so non-sensitive config fields like `secretRoleArn` stay
  // visible. (DEV-4723)
  // Multi-line values (e.g. a PEM private key) need a textarea — a single-line
  // input silently drops the newlines and corrupts the key. Checked before
  // password masking so the operator can verify a pasted key.
  if (isMultilineField(name, schema)) {
    return (
      <FormField label={label} hint={hint} error={error}>
        <TextArea
          aria-label={name}
          autoComplete="off"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </FormField>
    );
  }

  const password = schema.format === "password" || (maskSecrets && isPasswordField(name, schema));
  return (
    <FormField label={label} hint={hint} error={error}>
      <TextInput
        type={password ? "password" : "text"}
        aria-label={name}
        autoComplete={password ? "off" : undefined}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  );
}

function ArrayField({
  name,
  schema,
  required,
  ancestorOptional = false,
  value,
  error,
  onChange,
  widgets,
  widgetContext,
  errors,
}: {
  name: string;
  schema: JsonSchema;
  required: boolean;
  ancestorOptional?: boolean;
  value: unknown[] | undefined;
  error?: string;
  onChange: (v: unknown) => void;
  widgets?: Record<string, Widget>;
  widgetContext?: WidgetContext;
  errors?: Record<string, string>;
}) {
  const label = <FieldLabel name={name} required={required} conditional={ancestorOptional} />;
  const current = Array.isArray(value) ? value : [];
  const itemEnum = schema.items?.enum;
  if (itemEnum && itemEnum.length > 0) {
    // multi-select via checkboxes — every enum option as a row
    const selected = new Set(current.map((v) => String(v)));
    function toggle(option: string) {
      const next = new Set(selected);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      onChange(itemEnum!.filter((o) => next.has(o)));
    }
    return (
      <FormField label={label} hint={schema.description} error={error}>
        <div className="schema-form__checklist">
          {itemEnum.map((option) => (
            <label key={option} className="schema-form__checklist-row">
              <input
                type="checkbox"
                aria-label={`${name}.${option}`}
                checked={selected.has(option)}
                onChange={() => toggle(option)}
              />
              <span className="mono">{option}</span>
            </label>
          ))}
        </div>
      </FormField>
    );
  }
  // Array of objects (or discriminated unions): render an add/remove list of
  // cards, each recursing through SchemaForm. Fixes the comma-box degrade for
  // every object-array param. `items.oneOf` (a discriminated union) is an object
  // for our purposes — SchemaForm's own union path renders its `kind` tabs.
  const items = schema.items;
  const isObjectItem =
    !!items && (items.type === "object" || Array.isArray(items.oneOf) || Array.isArray(items.anyOf));
  if (isObjectItem) {
    function itemDefault(): unknown {
      if (items!.default !== undefined) return items!.default;
      return {};
    }
    function updateItem(i: number, v: unknown) {
      const next = current.slice();
      next[i] = v;
      onChange(next);
    }
    function addItem() {
      onChange([...current, itemDefault()]);
    }
    function removeItem(i: number) {
      onChange(current.filter((_, idx) => idx !== i));
    }
    return (
      <FormField label={label} hint={schema.description} error={error}>
        <div className="schema-form__array">
          {current.map((item, i) => (
            <div className="schema-form__array-item" key={i}>
              <div className="schema-form__array-item-head">
                <span className="uppercase-label">{`${name} ${i + 1}`}</span>
                <button
                  type="button"
                  className="btn btn--ghost"
                  aria-label={`remove ${name} ${i + 1}`}
                  onClick={() => removeItem(i)}
                >
                  REMOVE
                </button>
              </div>
              <SchemaForm
                schema={items as JsonSchema}
                value={(item as Record<string, unknown>) ?? {}}
                onChange={(v) => updateItem(i, v)}
                topLevel={false}
                ancestorOptional={ancestorOptional}
                widgets={widgets}
                widgetContext={widgetContext}
                errors={errors}
              />
            </div>
          ))}
          <button type="button" className="btn btn--secondary" onClick={addItem}>
            {`Add ${name}`}
          </button>
        </div>
      </FormField>
    );
  }
  // free-form string array — comma separated
  return (
    <FormField label={label} hint={schema.description ?? "Comma-separated values."} error={error}>
      <TextInput
        aria-label={name}
        value={current.map((v) => String(v)).join(", ")}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0),
          )
        }
      />
    </FormField>
  );
}

function RecordField({
  name,
  schema,
  required,
  ancestorOptional = false,
  value,
  error,
  onChange,
}: {
  name: string;
  schema: JsonSchema;
  required: boolean;
  ancestorOptional?: boolean;
  value: Record<string, unknown>;
  error?: string;
  onChange: (v: unknown) => void;
}) {
  // Draft state for a "new key" row so we don't update the underlying object
  // until both key and value are typed and the user clicks ADD.
  const [draftKey, setDraftKey] = useState("");
  const [draftVal, setDraftVal] = useState("");

  function setEntry(key: string, val: string) {
    onChange({ ...value, [key]: val });
  }
  function removeEntry(key: string) {
    const next = { ...value };
    delete next[key];
    onChange(next);
  }
  function addDraft() {
    if (!draftKey.trim()) return;
    setEntry(draftKey.trim(), draftVal);
    setDraftKey("");
    setDraftVal("");
  }

  const entries = Object.entries(value);

  return (
    <fieldset className="schema-form__nested">
      <legend className="uppercase-label">
        <FieldLabel name={name} required={required} conditional={ancestorOptional} />
        {!required && !ancestorOptional && <span className="field__conditional"> (optional)</span>}
      </legend>
      {schema.description && <div className="field__hint" style={{ marginBottom: 8 }}>{schema.description}</div>}
      <table className="table schema-form__record">
        <thead>
          <tr>
            <th style={{ width: "40%" }}>KEY</th>
            <th>VALUE</th>
            <th style={{ width: 60 }} aria-label="actions"></th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr>
              <td colSpan={3} className="mono" style={{ color: "var(--color-text-secondary)" }}>
                no entries
              </td>
            </tr>
          )}
          {entries.map(([k, v]) => (
            <tr key={k}>
              <td className="mono">{k}</td>
              <td>
                <TextInput
                  aria-label={`${name}.${k}`}
                  value={typeof v === "string" ? v : String(v ?? "")}
                  onChange={(e) => setEntry(k, e.target.value)}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => removeEntry(k)}
                  aria-label={`remove ${k}`}
                >
                  REMOVE
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <TextInput
                aria-label={`${name}.__newKey`}
                placeholder="key"
                value={draftKey}
                onChange={(e) => setDraftKey(e.target.value)}
              />
            </td>
            <td>
              <TextInput
                aria-label={`${name}.__newValue`}
                placeholder="value"
                value={draftVal}
                onChange={(e) => setDraftVal(e.target.value)}
              />
            </td>
            <td>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={addDraft}
                disabled={!draftKey.trim()}
              >
                ADD
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </fieldset>
  );
}
